import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import AuthLayout from "@container/layout/auth";
import TrainingAuthLayout from "@container/layout/training-auth";
import BillingWorkspaceActivity from "@container/billing-workspace-activity";
import { TabsContent } from "@radix-ui/react-tabs";
import { ActivityIcon, FileTextIcon, WalletIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { resolveBillingPlatform } from "@util/billing-platform";
import BillingPlanOverview from "@container/billing-plan-overview";
import BillingInvoice from "@container/billing-invoice";
import DialogViewPlan from "@container/dialog/dialog-view-plan";

type Currency = "SGD" | "MYR" | "IDR" | "USD";

// Whether the "Plan Overview" tab is shown. (It overlaps with the dedicated
// Subscription page; toggle to `false` to hide it again.)
const SHOW_PLAN_OVERVIEW = true;

// Tabs currently visible (depends on the flag above).
const ALLOWED_BILLING_TABS = SHOW_PLAN_OVERVIEW
    ? ["usage", "plan", "invoices"]
    : ["usage", "invoices"];

// Normalize a tab value (e.g. from the `?tab=` URL param) to a currently-visible
// tab, so a stale `?tab=plan` link doesn't land on the hidden tab and render a
// blank page. Falls back to "usage" (Workspace Activity — the default tab).
const normalizeBillingTab = (tab: string | null | undefined): string =>
    tab && ALLOWED_BILLING_TABS.includes(tab) ? tab : "usage";

export default function EmployerBillingPage() {
    const params = new URLSearchParams(window.location.search);
    const navigate = useNavigate();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const { subscriptionActive } = useSubscription();

    // Platform-aware chrome: training-agent gets the training sidebar, everyone
    // else (recruiter / agent) keeps the recruiter AuthLayout. The /billing route
    // is open to training-agent too, so this page must pick its own layout.
    const platform = useMemo(() => resolveBillingPlatform(userInfo), [userInfo]);
    const Layout = platform === "training" ? TrainingAuthLayout : AuthLayout;

    useEffect(() => {
        if (!isLoadingUserProfile && userInfo?.permission === "member") {
            navigate("/unauthorized", { replace: true });
        }
    }, [isLoadingUserProfile, userInfo?.permission]);
    const [activeTab, setActiveTab] = useState<string>("usage");
    const [showRequestInfoDialog, setShowRequestInfoDialog] = useState(false);
    const [requestInfoNote, setRequestInfoNote] = useState("");
    const [selectedPlan, setSelectedPlan] = useState<"12month" | "6month" | null>("12month");

    // Currency pricing based on reference table
    const currencyPricing: Record<Currency, {
        symbol: string;
        name: string;
        plans: {
            trial: number;
            "12month": number;
            "12monthTotal": number;
            "6month": number;
            "6monthTotal": number;
        };
        payAsYouGo: {
            cvScreening: number;
            aiAssessment: number;
        };
    }> = {
        SGD: {
            symbol: "SGD",
            name: "Singapore Dollar",
            plans: {
                trial: 0,
                "12month": 269,
                "12monthTotal": 3229,
                "6month": 299,
                "6monthTotal": 899,
            },
            payAsYouGo: {
                cvScreening: 1,
                aiAssessment: 5,
            },
        },
        MYR: {
            symbol: "RM",
            name: "Malaysian Ringgit",
            plans: {
                trial: 0,
                "12month": 899,
                "12monthTotal": 10789,
                "6month": 999,
                "6monthTotal": 2997,
            },
            payAsYouGo: {
                cvScreening: 3,
                aiAssessment: 15,
            },
        },
        IDR: {
            symbol: "IDR",
            name: "Indonesian Rupiah",
            plans: {
                trial: 0,
                "12month": 3150000,
                "12monthTotal": 37800000,
                "6month": 3500000,
                "6monthTotal": 10500000,
            },
            payAsYouGo: {
                cvScreening: 10000,
                aiAssessment: 50000,
            },
        },
        USD: {
            symbol: "USD",
            name: "US Dollar",
            plans: {
                trial: 0,
                "12month": 199,
                "12monthTotal": 2389,
                "6month": 219,
                "6monthTotal": 657,
            },
            payAsYouGo: {
                cvScreening: 1,
                aiAssessment: 5,
            },
        },
    };

    useEffect(() => {
        const tab = params.get("tab");
        if (tab) {
            // Map a stale/unsupported tab (e.g. `?tab=plan` while it's hidden)
            // to a visible one so the page never renders a blank tab body.
            setActiveTab(normalizeBillingTab(tab));
        }
    }, [params.get("tab")])

    const formatPrice = (price: number): string => {
        const { symbol } = currencyPricing[subscriptionActive?.company_currency as Currency || "MYR"];
        if (subscriptionActive?.company_currency === "IDR") {
            return `${symbol} ${Math.round(price).toLocaleString()}`;
        }
        return `${symbol} ${price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    };

    const getPlanMonthlyPrice = (plan: "12month" | "6month"): string => {
        const price = currencyPricing[subscriptionActive?.company_currency as Currency || "MYR"].plans[plan];
        return formatPrice(price);
    };

    const getPlanTotalPrice = (plan: "12month" | "6month"): string => {
        const priceKey = plan === "12month" ? "12monthTotal" : "6monthTotal";
        const price = currencyPricing[subscriptionActive?.company_currency as Currency || "MYR"].plans[priceKey];
        return formatPrice(price);
    };

    const getPayAsYouGoPrice = (type: "cvScreening" | "aiAssessment"): string => {
        const price = currencyPricing[subscriptionActive?.company_currency as Currency || "MYR"].payAsYouGo[type];
        return formatPrice(price);
    };

    const onChangeTab = (tab: string) => {
        setActiveTab(tab);
        const url = new URL(window.location.href);
        url.searchParams.set("tab", tab);
        window.history.pushState({}, "", url.toString());
    }

    return (
        <Layout>
            <Tabs value={activeTab} onValueChange={onChangeTab} className="w-full">
                <div className="flex items-center justify-between mb-6">
                    <TabsList className="flex w-auto bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
                        <TabsTrigger
                            value="usage"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <ActivityIcon className="h-4 w-4" />
                            Workspace Activity
                        </TabsTrigger>
                        {SHOW_PLAN_OVERVIEW && (
                            <TabsTrigger
                                value="plan"
                                className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                            >
                                <WalletIcon className="h-4 w-4" />
                                Plan Overview
                            </TabsTrigger>
                        )}
                        <TabsTrigger
                            value="invoices"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <FileTextIcon className="h-4 w-4" />
                            Invoices
                        </TabsTrigger>
                    </TabsList>
                </div>
                {/* Workspace Activity — shared platform-aware surface (recruiter
                    hiring activity / training activity), identical to the one on
                    the Subscription page's "Workspace Activity" tab. */}
                <TabsContent value="usage" className="mt-0">
                    <BillingWorkspaceActivity />
                </TabsContent>
                {SHOW_PLAN_OVERVIEW && (
                    <TabsContent value="plan" className="mt-0">
                        {/* Payment Details (Plans + Payment Method) is rendered
                            INSIDE BillingPlanOverview (Plans at the top). */}
                        <BillingPlanOverview
                            setShowRequestInfoDialog={setShowRequestInfoDialog}
                            formatPrice={formatPrice}
                        />
                    </TabsContent>
                )}
                <TabsContent value="invoices" className="mt-0">
                    <BillingInvoice
                        setShowRequestInfoDialog={setShowRequestInfoDialog}
                    />
                </TabsContent>
            </Tabs>
            <DialogViewPlan
                open={showRequestInfoDialog}
                onOpenChange={setShowRequestInfoDialog}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                requestInfoNote={requestInfoNote}
                setRequestInfoNote={setRequestInfoNote}
                getPlanMonthlyPrice={getPlanMonthlyPrice}
                getPlanTotalPrice={getPlanTotalPrice}
                getPayAsYouGoPrice={getPayAsYouGoPrice}
            />
        </Layout>
    )
}